import { useState, useEffect, useCallback } from 'react';
import { useNotify } from 'react-admin';
import authFetch from './authFetch';

/**
 * The business's current $→₪ conversion rate, persisted as the `usd_rate`
 * setting (admin-only to write, same as the existing `vat_rate`/`business_name`
 * settings) — used to manually convert a $ sale price into a ₪ one wherever
 * a line is priced in $ but its cost basis is in ₪.
 *
 * `rate` always reflects what's typed (updates on every keystroke so the
 * field feels responsive); the actual save only fires on blur, and only
 * when the value changed — typing "3.7" never persists the intermediate
 * "3" along the way. A rejected save (wrong role, out-of-range value, a
 * network failure) notifies and reverts the field to the last saved value,
 * instead of leaving the UI showing a rate that was never actually stored.
 */
export default function useUsdRate() {
  const notify = useNotify();
  const [rate, setRate] = useState('');
  const [savedRate, setSavedRate] = useState('');

  useEffect(() => {
    authFetch('/settings')
      .then(r => r.json())
      .then(d => { setRate(d.usd_rate || ''); setSavedRate(d.usd_rate || ''); })
      .catch(() => {});
  }, []);

  const onBlur = useCallback(async () => {
    if (rate === savedRate) return;
    if (!(Number(rate) > 0)) { setRate(savedRate); return; }
    try {
      const res = await authFetch('/settings', {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ usd_rate: rate }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        notify(data.error || 'שגיאה בשמירת שער הדולר — השינוי לא נשמר', { type: 'error' });
        setRate(savedRate);
        return;
      }
      setSavedRate(rate);
    } catch {
      notify('שגיאה בשמירת שער הדולר — השינוי לא נשמר', { type: 'error' });
      setRate(savedRate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rate, savedRate]);

  return [rate, setRate, onBlur];
}
